import {
	useMeetingsIntegrationsSettings,
	useUserVideoConferencingStatus,
	useVideoConferencingFeatureEnabled,
} from "../../../../api/hooks/defs/meetings-integrations";

import { useMeetingContext } from "../context";
import { type TranscriptBannerStateKind, deriveTranscriptBannerState } from "./transcript-banner-state";

/** Resolves the transcript banner state for the current meeting and viewer (creator-scoped). */
export const useTranscriptBannerState = (): TranscriptBannerStateKind => {
	const { meeting, userId, videoConferenceOwnerConnected } = useMeetingContext();

	const vcFeatureOn = useVideoConferencingFeatureEnabled();

	const { data: settings, isLoading: isSettingsLoading } = useMeetingsIntegrationsSettings({ enabled: vcFeatureOn });
	const companyVcEnabled = settings?.video_conferencing?.enabled === true;
	// The status endpoint is company-setting-gated (403s when VC is off), so only fire it once the
	// company has VC enabled — mirrors useVideoConferencingEligibility.
	// Attached conferences use the server-resolved hosting account. The current-user
	// status remains relevant only for discoverability, plus a compatibility fallback
	// while an older cached meeting-details response lacks the owner state.
	const needsCurrentUserStatus = !meeting.videoConference || videoConferenceOwnerConnected === undefined;
	const { data: status, isLoading: isStatusLoading } = useUserVideoConferencingStatus({
		enabled: vcFeatureOn && companyVcEnabled && needsCurrentUserStatus,
	});

	// Feature off, or the VC queries are still in flight → no chip/banner. Waiting for them to settle
	// avoids a flash of the wrong state on load (e.g. "disconnected" before connections resolve).
	if (!vcFeatureOn || isSettingsLoading || (needsCurrentUserStatus && isStatusLoading)) return "none";

	const scheduledEnd = new Date(meeting.endDateTime);
	const scheduledDurationMinutes = (scheduledEnd.getTime() - new Date(meeting.startDateTime).getTime()) / (60 * 1000);
	const now = new Date();

	const connectedProviders =
		meeting.videoConference && videoConferenceOwnerConnected !== undefined
			? videoConferenceOwnerConnected
				? [meeting.videoConference.provider]
				: []
			: (status?.connections ?? []).map((connection) => connection.provider);

	return deriveTranscriptBannerState({
		isCreator: meeting.created.userId === userId,
		videoConference: meeting.videoConference,
		companyVcEnabled,
		connectedProviders,
		meetingHasEnded: scheduledEnd.getTime() <= now.getTime(),
		scheduledEnd,
		scheduledDurationMinutes,
		createdAt: new Date(meeting.created.timeStamp),
		now,
	});
};
